import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import ConfirmModal from '../../incase-domain/components/modals/confirmModal/ConfirmModal';
import {PRESENTATION_ENDED_EVENT} from '../../constants';
import {getStoredRedirectUri, setStoredRedirectUri} from '../../util/redirect';

const REDIRECT_DELAY_SECONDS = 5;

function RedirectModal(): JSX.Element {
    const {t} = useTranslation();
    const [redirectUri, setRedirectUri] = useState<string | null>(null);
    const [secondsLeft, setSecondsLeft] = useState(REDIRECT_DELAY_SECONDS);
    const intervalRef = useRef<ReturnType<typeof setInterval>>();

    useEffect(() => {
        const handlePresentationEnded = (): void => {
            const storedUri = getStoredRedirectUri();
            if (storedUri) {
                setSecondsLeft(REDIRECT_DELAY_SECONDS);
                setRedirectUri(storedUri);
            }
        };

        window.addEventListener(PRESENTATION_ENDED_EVENT, handlePresentationEnded);
        return () => window.removeEventListener(PRESENTATION_ENDED_EVENT, handlePresentationEnded);
    }, []);

    useEffect(() => {
        if (!redirectUri) return undefined;

        intervalRef.current = setInterval(() => {
            setSecondsLeft((prev) => prev - 1);
        }, 1000);
        return () => clearInterval(intervalRef.current);
    }, [redirectUri]);

    const stopCountdown = (): void => {
        if (intervalRef.current) clearInterval(intervalRef.current);
    };

    const goToRedirectUri = (uri: string): void => {
        stopCountdown();
        setStoredRedirectUri(null);
        window.location.href = uri;
    };

    const cancelRedirect = (): void => {
        stopCountdown();
        setStoredRedirectUri(null);
        setRedirectUri(null);
    };

    useEffect(() => {
        if (redirectUri && secondsLeft <= 0) goToRedirectUri(redirectUri);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [secondsLeft, redirectUri]);

    return (
        <ConfirmModal
            isOpen={!!redirectUri}
            color="info"
            title={t('playerPage.redirecting.title')}
            message={t('playerPage.redirecting.description', {seconds: Math.max(secondsLeft, 0)})}
            confirmText={t('playerPage.redirecting.returnNow')}
            onConfirm={() => redirectUri && goToRedirectUri(redirectUri)}
            showCancel
            onCancel={cancelRedirect}
        />
    );
}

export default RedirectModal;
